import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAiChatStore } from '../stores/useAiChatStore';
import { sendChatToAi } from '../services/aiAgentApi';
import { api } from '../../../lib/api';

export const useAiChat = () => {
  const { t } = useTranslation('shop');
  const { messages, sessionId, addMessage, setLoading, isLoading } = useAiChatStore();
  const [error, setError] = useState<string | null>(null);

  const sendMessage = async (text: string, customWebhookUrl?: string) => {
    if (!text.trim()) return;

    // 1. Add user message
    addMessage({ role: 'user', text });
    setLoading(true);
    setError(null);

    // 2. Check for manual phone capture in user text (Egyptian number pattern)
    const phoneMatch = text.match(/01[0-9]{9}/);
    if (phoneMatch) {
      // Auto-capture silently in background if they typed a phone number
      try {
        await api.post('/leads/capture', {
          phone: phoneMatch[0],
          sessionId,
          source: 'CHATBOT'
        });
      } catch (e) {
        // Ignore silent capture errors
      }
    }

    try {
      // 3. Send to AI
      const response = await sendChatToAi(text, sessionId, customWebhookUrl);
      
      let botText = response.output || '';
      let products: any[] = [];

      // 4. Extract Products Block
      const productsMatch = botText.match(/<!--PRODUCTS:(\[.*?\])-->/s);
      if (productsMatch && productsMatch[1]) {
        try {
          products = JSON.parse(productsMatch[1]);
        } catch (e) {
          console.error("Failed to parse AI products JSON", e);
        }
        // Remove the block from visible text
        botText = botText.replace(/<!--PRODUCTS:\[.*?\]-->/s, '').trim();
      }

      // 5. Extract Lead Capture Block
      const leadMatch = botText.match(/<!--LEAD:(\{.*?\})-->/s);
      if (leadMatch && leadMatch[1]) {
        try {
          const leadData = JSON.parse(leadMatch[1]);
          // Fire API call to backend
          api.post('/leads/capture', {
            phone: leadData.phone,
            name: leadData.name,
            sessionId,
            source: 'CHATBOT',
            chatSummary: `User expressed interest. Phone collected: ${leadData.phone}`
          }).catch(console.error);
        } catch (e) {
          console.error("Failed to parse AI lead JSON", e);
        }
        // Remove the block from visible text
        botText = botText.replace(/<!--LEAD:\{.*?\}-->/s, '').trim();
      }

      // 6. Add bot message
      addMessage({ role: 'bot', text: botText, products });

    } catch (err: any) {
      setError(err.message || 'Something went wrong while thinking...');
      addMessage({ role: 'error', text: t('aiChat.errorConnecting', 'Sorry, I am having trouble connecting right now. Please try again later.') });
    } finally {
      setLoading(false);
    }
  };

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    sessionId
  };
};
